import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { TovSourceStatus } from "contracts/enums/tov";
import { TOV_USABLE_SOURCE_STATUSES } from "contracts/enums/tov";
import {
  guidelineText,
  hasUnrelatedBrandReference,
} from "../tov/guidelineModel";

export function sourceCoverage(
  sources: Array<{ status: TovSourceStatus }> | undefined,
): string {
  if (!sources) return "Checking source state";
  if (sources.length === 0) return "No sources";
  const usable = sources.filter((source) =>
    TOV_USABLE_SOURCE_STATUSES.includes(source.status),
  ).length;
  return `${usable}/${sources.length} sources parsed`;
}

export function traceabilityStatus(
  version: TovVersionResponse | undefined,
  sources: TovSourceResponse[] | undefined,
): string {
  if (!version) return "Checking guidance";
  if (version.rules.length === 0) return "Not configured";
  if (linkedEvidenceCount(version) === 0) return "Rules stored";
  if (!sources) return "Checking source linkage";
  const sourceIds = new Set(sources.map((source) => source.id));
  return linkedEvidenceSourceIds(version).some((id) => !sourceIds.has(id))
    ? "Source not established"
    : "Source evidence linked";
}

function linkedEvidenceCount(version: TovVersionResponse): number {
  return linkedEvidenceSourceIds(version).length;
}

export function establishedLinkedEvidenceCount(
  version: TovVersionResponse,
  sources: TovSourceResponse[] | undefined,
): number {
  if (!sources) return 0;
  const sourceIds = new Set(sources.map((source) => source.id));
  return linkedEvidenceSourceIds(version).filter((id) => sourceIds.has(id))
    .length;
}

function linkedEvidenceSourceIds(version: TovVersionResponse): string[] {
  const direct = [
    ...version.traits,
    ...version.toneDimensions,
    ...version.rules,
    ...version.lexicon,
    ...version.examples,
    ...version.audiences,
    ...version.contentPillars,
    ...version.journeyStages,
    ...version.channels,
    ...version.visualGuidelines,
  ].flatMap((item) =>
    item.linkedEvidence.map((link) => link.evidence.sourceId),
  );
  const boundaries = version.traits.flatMap((trait) =>
    trait.boundaries.flatMap((boundary) =>
      boundary.linkedEvidence.map((link) => link.evidence.sourceId),
    ),
  );
  return [...direct, ...boundaries];
}

export function sourceCountLabel(
  sources: TovSourceResponse[] | undefined,
): string {
  return sources ? `${sources.length}` : "Checking";
}

export function versionStatusLabel(
  version: TovVersionResponse | undefined,
): string {
  return version
    ? version.status[0].toUpperCase() + version.status.slice(1)
    : "Checking";
}

export function populatedGuidanceRows(version: TovVersionResponse | undefined) {
  if (!version) return [];
  return [
    { label: "traits", value: version.traits.length },
    { label: "tone dimensions", value: version.toneDimensions.length },
    { label: "rules", value: version.rules.length },
    { label: "lexicon entries", value: version.lexicon.length },
    { label: "examples", value: version.examples.length },
    { label: "audiences", value: version.audiences.length },
    { label: "content pillars", value: version.contentPillars.length },
    { label: "journey stages", value: version.journeyStages.length },
    { label: "channel definitions", value: version.channels.length },
    { label: "visual guidelines", value: version.visualGuidelines.length },
  ].filter((row) => row.value > 0);
}

export function sourceTrustStatus(
  data: OverviewResponse,
  sources: TovSourceResponse[] | undefined,
  version: TovVersionResponse | undefined,
): string {
  if (!version) return "Checking";
  if (!sources || sources.length === 0) return "Source not established";
  const text = [
    ...sources.map((source) => source.title),
    ...guidelineText(version),
  ].join(" ");
  return hasUnrelatedBrandReference(data.site, text)
    ? "Source review required"
    : "Source evidence";
}
